/**
 * Asks for one line of text: a folder's name, a link's address. In place of
 * `window.prompt`, which the desktop app's web view does not show at all, so
 * New folder there did nothing. Answers the text, or null when cancelled.
 */

import { useEffect, useRef, useState } from 'react';
import { createRoot } from 'react-dom/client';

type Ask = {
  /** The question, e.g. "New folder". */
  title: string;
  /** The field's label, e.g. "Name". */
  label: string;
  initial?: string;
  /** The button that answers, e.g. "Create". */
  action?: string;
};

function AskTextDialog({
  title,
  label,
  initial = '',
  action = 'OK',
  onDone,
}: Ask & { onDone: (answer: string | null) => void }): JSX.Element {
  const [value, setValue] = useState(initial);
  const field = useRef<HTMLInputElement>(null);
  useEffect(() => {
    field.current?.focus();
    field.current?.select();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onDone(null);
      }
    };
    document.addEventListener('keydown', onKey, true);
    return () => document.removeEventListener('keydown', onKey, true);
  }, [onDone]);

  return (
    <div className="modal-scrim" role="presentation">
      <form
        className="close-dialog ask-text"
        role="dialog"
        aria-modal="true"
        aria-labelledby="ask-text-title"
        // Keys typed here stay here: the composer's shortcuts must not fire.
        onKeyDown={(e) => e.stopPropagation()}
        onSubmit={(e) => {
          e.preventDefault();
          onDone(value);
        }}
      >
        <h3 id="ask-text-title">{title}</h3>
        <label>
          {label}
          <input ref={field} value={value} onChange={(e) => setValue(e.target.value)} />
        </label>
        <div className="close-dialog-acts">
          <button type="button" className="btn ghost" onClick={() => onDone(null)}>
            Cancel
          </button>
          <button type="submit" className="btn">
            {action}
          </button>
        </div>
      </form>
    </div>
  );
}

/** Shows the question over the page until it is answered or cancelled. */
export function askText(ask: Ask): Promise<string | null> {
  return new Promise((resolve) => {
    const host = document.createElement('div');
    document.body.append(host);
    const root = createRoot(host);
    const done = (answer: string | null) => {
      root.unmount();
      host.remove();
      resolve(answer);
    };
    root.render(<AskTextDialog {...ask} onDone={done} />);
  });
}
